import { request } from './core';
import * as ProcessingResultTypes from '@/@types/processingResultsTypes';
import * as TaskTypes from '@/@types/tasksTypes';

/**
 * Fetches the in-order list of processing results for a given task id. Specific actions can cause results
 * to be reprocessed, resulting in multiple versions.
 * @param {string} taskId
 * @returns {Promise<ProcessingResultTypes.ProcessingResult[]>}
 */
export const fetchProcessingResultsForTask = (taskId) => {
  return request(`processing-results`, {
    method: 'GET',
    params: {
      task_id: taskId
    }
  });
};

/**
 * Fetches the result details for a specific result id
 * @param {string} resultId
 * @returns {Promise<ProcessingResultTypes.ProcessingResult & ProcessingResultTypes.ProcessingResultDetails>}
 */
export const fetchResult = (resultId) => {
  return request(`processing-results/${resultId}`, {
    method: 'GET'
  });
};

/**
 * Fetches the analyze and import statuses associated with the specified result
 * @param {string} resultId
 * @param {string} environment
 * @returns {Promise<ProcessingResultTypes.ProcessingResultStatuses>}
 */
export const fetchAnalyzeAndImportStatuses = (resultId, environment) => {
  return request(`processing-results/${resultId}/statuses`, {
    method: 'GET',
    params: { environment }
  });
};

/**
 * @typedef {Object} ResultRowFilterableOptions
 * @property {string[]=} statuses
 * @property {string[]=} statusesNames
 * @property {string=} search
 * @property {string[]=} categories
 * @property {Object=} exclude - String representation of JSON object with optional keys 'multimatch' and 'statusesNames', both with a value of an array of strings
 * @property {string=} searchContext
 * @property {boolean=} reviewed
 * @property {boolean=} customerVerified
 * @property {boolean=} lastSold - True if the last sold info should be included in each row
 * @property {string[]=} multimatch
 * @property {string[]=} rows_ids
 */

/**
 * @typedef {Object} FetchResultsRowRequestContext
 * @property {string=} comment
 * @property {string=} conflicts
 * @property {boolean=} descending
 * @property {boolean=} fetchAll
 * @property {string[]=} groupBy
 * @property {number=} page
 * @property {number=} rowsPerPage
 * @property {string=} sortBy
 */

/**
 * @typedef {Object} FetchProcessingResultRowsPaginatedResponse
 * @property {ProcessingResultTypes.ProcessingResultRow[]} items
 * @property {{
 *     page: number;
 *     pageSize: number;
 *     rowCount: number;
 *     pageCount: number;
 * }}
 */

/**
 *
 * @param {string} resultId
 * @param {FetchResultsRowRequestContext & ResultRowFilterableOptions} params
 * @param {boolean} fetchAll
 * @returns {Promise<FetchProcessingResultRowsPaginatedResponse>}
 */
export const fetchRows = (resultId, params, fetchAll) => {
  return request(`processing-results/${resultId}/rows`, {
    method: 'GET',
    params: {
      fetchAll,
      ...params
    }
  });
};

/**
 *
 * @param {Object} FetchResultRowCountRequestParams
 * @param {string} FetchResultRowCountRequestParams.resultId
 * @param {'status' | 'analyse' | 'import'} FetchResultRowCountRequestParams.operation
 * @param {string} FetchResultRowCountRequestParams.environment
 * @param {ResultRowFilterableOptions} FetchResultRowCountRequestParams.resultContextParams
 * @returns {Promise<number>}
 */
export const fetchResultRowCount = ({
  resultId,
  operation,
  environment,
  resultContextParams
}) => {
  return request(`processing-results/${resultId}/rows/count`, {
    method: 'GET',
    params: {
      operation,
      environment,
      ...resultContextParams
    }
  });
};

/**
 * Update the specified row with a partial row object
 * @param {string} resultId
 * @param {string} rowId
 * @param {Partial<ProcessingResultTypes.ProcessingResultRow>} partialUpdatePart
 * @returns {Promise<ProcessingResultTypes.ProcessingResultRow>} the updated row
 */
export const updateRow = (resultId, rowId, partitalRow) => {
  return request(`processing-results/${resultId}/rows/${rowId}`, {
    method: 'PUT',
    data: partitalRow
  });
};

/**
 * Fetches the match score statistics for where we think we found exact matches
 * @param {string} resultId
 * @returns {Promise<{
 *    matchesAmount: string,
 *    multimatchWithMatches: number
 *}>}
 */
export const fetchMatchScoreStatistics = (resultId) => {
  return request(`processing-results/${resultId}/match-score/statistics`, {
    method: 'GET'
  });
};

/**
 * Retrieves the number of result rows that are applicable for a smart status change
 * (ie. they solf in the last 36 months and do not have any multi-matches)
 * @param {string} resultId
 * @returns {Promise<string>}
 */
export const fetchSmartStatusRowsAmount = (resultId) => {
  return request(
    `processing-results/${resultId}/match-score/smart-status/amount`,
    {
      method: 'GET'
    }
  );
};

/**
 * Updates the status of all rows that are eligible for smart status changes to 'Ready for import'
 * @param {string} resultId
 * @returns {Promise<string>} A string indicated the update was successful
 */
export const applySmartStatus = (resultId) => {
  return request(`processing-results/${resultId}/match-score/smart-status`, {
    method: 'POST'
  });
};

/**
 * Generates a secure link for the customer to view the processing results and returns the hash id of the
 * generated link
 * @param {string} resultId
 * @param {'protected_link' | 'mig_pal_link'} linkType
 * @returns {Promise<string>}
 */
export const fetchCustomerLinkAccessToken = (resultId, linkType) => {
  return request(`processing-results/${resultId}/link`, {
    method: 'POST',
    data: {
      linkType
    }
  });
};

/**
 * Fetches all distinct comments for a given result and category
 * e.g. Could be used to retrieve the array of distinct comments for all ignored results
 * @param {string} resultId
 * @param {string} category
 * @returns {Promise<string[]>}
 */
export const fetchResultRowsUniqueComments = (resultId, category) => {
  return request(`processing-results/${resultId}/comments`, {
    method: 'GET',
    params: {
      category
    }
  });
};

/**
 * Returns a response of all comments on a result. Although, this endpoint doesn't actually
 * appear to use pagination.
 * @param {string} resultId
 * @param {string} rowId
 * @returns {Promise<{ items: ProcessingResultTypes.ProcessingResultRowComment[]}>}
 */
export const fetchResultRowComments = (resultId, rowId) => {
  return request(`processing-results/${resultId}/rows/${rowId}/comments`, {
    method: 'GET'
  });
};

/**
 * @param {Object} AddResultRowCommentRequestParams
 * @param {string} AddResultRowCommentRequestParams.resultId
 * @param {string} AddResultRowCommentRequestParams.rowId
 * @param {string} AddResultRowCommentRequestParams.message
 * @param {string=} AddResultRowCommentRequestParams.statusId
 * @returns {Promise<ProcessingResultTypes.ProcessingResultRowComment>} The new comment
 */
export const addResultRowComment = ({ resultId, rowId, message, statusId }) => {
  return request(`processing-results/${resultId}/rows/${rowId}/comments`, {
    method: 'POST',
    data: {
      message,
      status_id: statusId
    }
  });
};

/**
 * Returns the total percent of revenue captured by the existing line by summing the revenue value specified in the input
 * @param {string} resultId
 * @returns {Promise<number>}
 */
export const fetchTotalRevenue = (resultId) => {
  return request(`processing-results/${resultId}/total-revenue`, {
    method: 'GET'
  });
};

/**
 * @param {string} resultId
 * @returns {Promise<TaskTypes.MatcherTaskResultStatistics>}
 */
export const fetchResultStatistics = (resultId) => {
  return request(`processing-results/${resultId}/statistics`, {
    method: 'GET'
  });
};

/**
 * @param {Object} UpdateResultRowStatusRequestParams
 * @param {string} UpdateResultRowStatusRequestParams.resultId
 * @param {string[]} UpdateResultRowStatusRequestParams.rowIds
 * @param {string} UpdateResultRowStatusRequestParams.status
 * @param {Object} UpdateResultRowStatusRequestParams.filters
 * @returns {Promise<ProcessingResultTypes.ProcessingResultRowStatus>}
 */
export const updateResultRowStatus = ({
  resultId,
  rowIds,
  status,
  filters
}) => {
  return request(`processing-results/${resultId}/rows/statuses`, {
    method: 'POST',
    data: {
      rows_ids: rowIds,
      status,
      filters
    }
  });
};

/**
 *
 * @param {Object} BulkUpdateResultRowStatusRequestParams
 * @param {string} BulkUpdateResultRowStatusRequestParams.resultId
 * @param {string} BulkUpdateResultRowStatusRequestParams.status
 * @param {string} BulkUpdateResultRowStatusRequestParams.search
 * @param {string[]} BulkUpdateResultRowStatusRequestParams.statuses
 * @param {FetchResultsRowRequestContext & ResultRowFilterableOptions} BulkUpdateResultRowStatusRequestParams.resultContextParams
 * @returns
 */
export const bulkUpdateResultRowStatus = ({
  resultId,
  status,
  search,
  statuses,
  resultContextParams
}) => {
  return request(`processing-results/${resultId}/rows/statuses/bulk`, {
    method: 'POST',
    data: {
      status,
      search,
      statuses,
      ...resultContextParams
    }
  });
};

/**
 *
 * @param {Object} UpdateResultRowPropsStucture
 * @param {string} UpdateResultRowPropsStucture.resultId
 * @param {string} UpdateResultRowPropsStucture.rowId
 * @param {string} UpdateResultRowPropsStucture.propsId
 * @param {Partial<ProcessingResultTypes.ProcessingResultRowPropsStructure>} UpdateResultRowPropsStucture.updatedProps
 * @returns {Promise<ProcessingResultTypes.ProcessingResultRowPropsStructure>} the updated props structure
 */
export const updateResultRowPropsStucture = ({
  resultId,
  rowId,
  propsId,
  updatedProps
}) => {
  return request(
    `processing-results/${resultId}/rows/${rowId}/props-structure/${propsId}`,
    {
      method: 'PUT',
      data: updatedProps
    }
  );
};

/**
 * @param {Object} StartAnalyzeRowsRequestParams
 * @param {string} StartAnalyzeRowsRequestParams.resultId
 * @param {string} StartAnalyzeRowsRequestParams.environment
 * @param {ResultRowFilterableOptions} StartAnalyzeRowsRequestParams.resultContextParams
 * @returns {Promise<string>} a string indicating the analysis started
 */
export const startAnalyzeRows = ({
  resultId,
  environment,
  resultContextParams
}) => {
  return request(`processing-results/${resultId}/rows/analyse/bulk`, {
    method: 'POST',
    data: {
      environment,
      ...resultContextParams
    }
  });
};

/**
 * @param {Object} StartImportRowsRequestParams
 * @param {string} StartImportRowsRequestParams.resultId
 * @param {string} StartImportRowsRequestParams.environment
 * @param {ResultRowFilterableOptions} StartImportRowsRequestParams.resultContextParams
 * @returns {Promise<string>} a string indicating the import started
 */
export const startImportRows = ({
  resultId,
  environment,
  resultContextParams
}) => {
  return request(`processing-results/${resultId}/rows/import/bulk`, {
    method: 'POST',
    data: {
      environment,
      ...resultContextParams
    }
  });
};
